import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';

import { palette } from '@src/theme';
import { privatePalette } from '@src/theme/privatePalette';
import { getTestId } from '@src/utils/testIds';
import { CircularProgress } from '../CircularProgress/CircularProgress.web';
import { BaseButtonProps } from './interface';
import { calculateLoadingSpinnerColor } from './utils';

export const BaseButton = (props: BaseButtonProps) => {
  const muiProps = medelyPropsToMuiProps(props);
  return (
    <MuiButton
      {...muiProps}
      fullWidth={props.fullWidth}
      data-testid={getTestId(props)}
      disableElevation
    >
      {props.children}
    </MuiButton>
  );
};

const SX_MAPPING: Record<'contained' | 'text' | 'outlined', Record<string, any>> = {
  contained: {
    primary: {
      backgroundColor: palette.state.teal.primary,
      color: palette.surface.paper,
      '&:hover': {
        backgroundColor: privatePalette.buttonBg.hover.containedPrimary,
      },
      '&:active': {
        backgroundColor: privatePalette.buttonBg.pressed.containedPrimary,
      },
      '&:focus': {
        border: `2px solid ${palette.state.teal.secondary}`,
      },
    },
    secondary: {
      backgroundColor: palette.state.teal['backing-1'],
      color: palette.state.teal.secondary,
      '&:hover': {
        backgroundColor: privatePalette.buttonBg.hover.containedSecondary,
      },
      '&:active': {
        backgroundColor: privatePalette.buttonBg.pressed.containedSecondary,
      },
      '&:focus': {
        border: `2px solid ${palette.state.teal.secondary}`,
      },
    },
    destructive: {
      backgroundColor: palette.state.error.secondary,
      color: palette.surface.paper,
      '&:hover': {
        backgroundColor: privatePalette.buttonBg.hover.containedDesctructive,
      },
      '&:active': {
        backgroundColor: palette.state.error.primary,
      },
      '&:focus': {
        border: `2px solid ${palette.state.error.primary}`,
      },
    },
  },
  text: {
    primary: {
      backgroundColor: undefined,
      color: palette.state.teal.secondary,
      '&:hover': {
        backgroundColor: palette.state.teal.backing,
      },
      '&:active': {
        backgroundColor: palette.state.teal['backing-1'],
      },
      '&:focus': {
        border: `2px solid ${palette.state.teal.secondary}`,
      },
    },
    // text secondary isn't actually used...
    secondary: {
      backgroundColor: undefined,
      color: palette.state.teal.secondary,
      '&:hover': {
        backgroundColor: palette.state.teal.backing,
      },
      '&:active': {
        backgroundColor: palette.state.teal['backing-1'],
      },
      '&:focus': {
        borderColor: palette.state.teal.secondary,
        borderWidth: '2px',
      },
    },
    destructive: {
      backgroundColor: undefined,
      color: palette.state.error.secondary,
      '&:hover': {
        backgroundColor: palette.state.error.backing,
      },
      '&:active': {
        backgroundColor: palette.state.error['backing-1'],
      },
      '&:focus': {
        borderColor: palette.state.error.primary,
        borderWidth: '2px',
      },
    },
  },
  outlined: {
    default: {
      backgroundColor: undefined,
      color: palette.text.secondary,
      borderColor: palette.controls.outline,
      borderWidth: '1px',
      '&:hover': {
        borderColor: palette.controls.outline,
        backgroundColor: palette.state.teal.backing,
      },
      '&:active': {
        backgroundColor: palette.state.teal['backing-1'],
      },
      '&:focus': {
        borderColor: palette.state.teal.secondary,
        borderWidth: '2px',
      },
    },
  },
};

const medelyPropsToMuiProps = (medelyProps: BaseButtonProps): MuiButtonProps => {
  const sx = buildSx(medelyProps);
  const { startIcon, onClick } = medelyProps;
  const loadingSpinnerColor = calculateLoadingSpinnerColor(medelyProps);
  return {
    variant: medelyProps.variant ?? 'contained',
    startIcon,
    endIcon: medelyProps.loading ? (
      <CircularProgress size={24} sx={{ color: loadingSpinnerColor }} />
    ) : (
      medelyProps.endIcon
    ),
    onClick,
    disabled: medelyProps.disabled || medelyProps.loading,
    type: medelyProps.type,
    sx,
  };
};

const buildSx = (medelyProps: BaseButtonProps): MuiButtonProps['sx'] => {
  const isContainedVariant = !medelyProps.variant || medelyProps.variant === 'contained';
  if (isContainedVariant) {
    const sx = SX_MAPPING.contained[medelyProps.color!] ?? SX_MAPPING.contained.primary;
    sx['&.Mui-disabled'] = medelyProps.loading
      ? { backgroundColor: sx.backgroundColor, color: sx.color }
      : undefined;
    return sx;
  } else if (medelyProps.variant === 'text') {
    const sx = SX_MAPPING.text[medelyProps.color!] ?? SX_MAPPING.text.primary;
    sx['&.Mui-disabled'] = medelyProps.loading
      ? { backgroundColor: sx.backgroundColor, color: sx.color }
      : undefined;
    return sx;
  } else if (medelyProps.variant === 'outlined') {
    const sx = SX_MAPPING.outlined.default;
    sx['&.Mui-disabled'] = medelyProps.loading
      ? { backgroundColor: sx.backgroundColor, color: sx.color }
      : undefined;
    return sx;
  }
  return {};
};
